import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Target, Plus, TrendingUp, Calendar, Lightbulb, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const GoalsSection = ({ startupId, currency }) => {
  const [goals, setGoals] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewGoal, setShowNewGoal] = useState(false);
  const [optedIn, setOptedIn] = useState(false);
  const [newGoal, setNewGoal] = useState({
    goal_type: 'revenue',
    target_value: ''
  });

  useEffect(() => {
    if (startupId) {
      loadGoalsAndSuggestions();
      checkOptInStatus();
    }
  }, [startupId]);

  const checkOptInStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setOptedIn(response.data.opt_in_coaching || false);
    } catch (error) {
      console.error('Failed to check opt-in status');
    }
  };

  const loadGoalsAndSuggestions = async () => {
    setLoading(true);
    try {
      const [goalsRes, suggestionsRes] = await Promise.all([
        axios.get(`${API}/startups/${startupId}/goals`, { withCredentials: true }),
        axios.get(`${API}/coaching/my-suggestions`, { withCredentials: true })
      ]);
      setGoals(goalsRes.data);
      setSuggestions(suggestionsRes.data);
    } catch (error) {
      console.error('Failed to load goals');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGoal = async () => {
    if (!newGoal.target_value || newGoal.target_value <= 0) {
      toast.error('Please enter a valid target value');
      return;
    }

    try {
      await axios.post(
        `${API}/startups/${startupId}/goals`,
        {
          startup_id: startupId,
          goal_type: newGoal.goal_type,
          target_value: parseFloat(newGoal.target_value),
          current_value: 0
        },
        { withCredentials: true }
      );
      
      toast.success('Goal created successfully!');
      setShowNewGoal(false);
      setNewGoal({ goal_type: 'revenue', target_value: '' });
      loadGoalsAndSuggestions();
    } catch (error) {
      toast.error('Failed to create goal');
    }
  };

  const handleRequestActionPlan = async (goalId) => {
    if (!optedIn) {
      toast.error('Please opt-in to coaching first');
      return;
    }

    try {
      await axios.post(
        `${API}/coaching/request-action-plan`,
        {
          startup_id: startupId,
          goal_id: goalId,
          request_type: 'action_plan'
        },
        { withCredentials: true }
      );
      
      toast.success('Action plan requested! A coach will respond soon.');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to request action plan';
      toast.error(errorMsg);
    }
  };

  const handleToggleCoaching = async () => {
    try {
      await axios.patch(
        `${API}/users/coaching-opt-in`,
        { opt_in: !optedIn },
        { 
          withCredentials: true,
          params: { opt_in: !optedIn }
        }
      );
      setOptedIn(!optedIn);
      toast.success(optedIn ? 'Coaching opt-out successful' : 'Coaching opt-in successful!');
    } catch (error) {
      toast.error('Failed to update coaching preference');
    }
  };

  const formatCurrency = (value) => {
    const symbols = { USD: '$', EUR: '€', GBP: '£', JPY: '¥', INR: '₹', CAD: 'C$', AUD: 'A$' };
    const symbol = symbols[currency] || '$';
    return `${symbol}${value.toLocaleString()}`;
  };

  if (loading) {
    return (
      <Card className="bg-slate-800/40 border-slate-700 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cyan-400"></div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="goals-section">
      {/* Header with Opt-in Toggle */}
      <Card className="bg-slate-800/40 border-slate-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Target className="w-6 h-6 text-cyan-400" />
            <h3 className="text-xl font-semibold text-slate-100">Goals & Coaching</h3>
          </div>
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={optedIn}
                onChange={handleToggleCoaching}
                data-testid="coaching-opt-in"
                className="w-4 h-4 text-cyan-600 rounded focus:ring-cyan-500"
              />
              <span className="text-sm text-slate-300">Enable Coaching</span>
            </label>
            <Dialog open={showNewGoal} onOpenChange={setShowNewGoal}>
              <DialogTrigger asChild>
                <Button data-testid="create-goal-btn" className="bg-gradient-to-r from-cyan-500 to-blue-600">
                  <Plus className="w-4 h-4 mr-2" />
                  New Goal
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-700" aria-describedby="new-goal-description">
                <DialogHeader>
                  <DialogTitle className="text-slate-100">Create New Goal</DialogTitle>
                </DialogHeader>
                <p id="new-goal-description" className="sr-only">Set a target goal for your startup</p>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="text-slate-300">Goal Type</Label>
                    <select
                      value={newGoal.goal_type}
                      onChange={(e) => setNewGoal({ ...newGoal, goal_type: e.target.value })}
                      data-testid="goal-type-select"
                      className="w-full mt-1 bg-slate-800 border border-slate-700 text-slate-100 rounded-md px-3 py-2"
                    >
                      <option value="revenue">Revenue Target</option>
                      <option value="units">Units Sold Target</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-300">
                      {newGoal.goal_type === 'revenue' ? 'Target Revenue' : 'Target Units'}
                    </Label>
                    <Input
                      type="number"
                      value={newGoal.target_value}
                      onChange={(e) => setNewGoal({ ...newGoal, target_value: e.target.value })}
                      data-testid="goal-target-input"
                      className="bg-slate-800 border-slate-700 text-slate-100"
                      placeholder={newGoal.goal_type === 'revenue' ? '100000' : '1000'}
                    />
                  </div>
                  <Button
                    onClick={handleCreateGoal}
                    data-testid="submit-goal-btn"
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600"
                  >
                    Create Goal
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </Card>

      {/* Goals List */}
      {goals.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4">
          {goals.map((goal) => (
            <Card key={goal.id} className="bg-slate-800/40 border-slate-700 p-6" data-testid={`goal-${goal.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-100">
                      {goal.goal_type === 'revenue' ? 'Revenue Goal' : 'Units Goal'}
                    </h4>
                    <p className="text-2xl font-bold text-cyan-400">
                      {goal.goal_type === 'revenue' 
                        ? formatCurrency(goal.target_value)
                        : `${goal.target_value.toLocaleString()} units`
                      }
                    </p>
                  </div>
                </div>
                {goal.status === 'active' && (
                  <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded-full border border-green-500/30">
                    Active
                  </span>
                )}
              </div>

              {goal.estimated_achievement_date && (
                <div className="flex items-center space-x-2 mb-4 text-slate-300">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">
                    Est. Achievement: {new Date(goal.estimated_achievement_date).toLocaleDateString()}
                  </span>
                </div>
              )}

              <Button
                onClick={() => handleRequestActionPlan(goal.id)}
                disabled={!optedIn}
                data-testid={`request-plan-${goal.id}`}
                variant="outline"
                className="w-full border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
              >
                <Lightbulb className="w-4 h-4 mr-2" />
                {optedIn ? 'Request Action Plan' : 'Enable Coaching First'}
              </Button>
            </Card>
          ))}
        </div>
      )}

      {/* Coaching Suggestions */}
      {suggestions.length > 0 && (
        <Card className="bg-slate-800/40 border-slate-700 p-6">
          <h4 className="font-semibold text-slate-100 mb-4 flex items-center">
            <CheckCircle2 className="w-5 h-5 mr-2 text-green-400" />
            Coach Suggestions
          </h4>
          <div className="space-y-4">
            {suggestions.map((suggestion) => (
              <div
                key={suggestion.id}
                className="bg-slate-700/30 rounded-lg p-4 border-l-4 border-green-400"
                data-testid={`suggestion-${suggestion.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">
                    For: {suggestion.startup_name}
                  </span>
                  <span className="text-xs text-slate-500">
                    {new Date(suggestion.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-slate-200 whitespace-pre-wrap">{suggestion.suggestion_text}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Empty State */}
      {goals.length === 0 && (
        <Card className="bg-slate-800/40 border-slate-700 p-12">
          <div className="text-center">
            <Target className="w-16 h-16 mx-auto mb-4 text-slate-600" />
            <h4 className="text-lg font-semibold text-slate-300 mb-2">No Goals Yet</h4>
            <p className="text-slate-400 mb-6">Set your first goal to get achievement estimates!</p>
            <Button
              onClick={() => setShowNewGoal(true)}
              className="bg-gradient-to-r from-cyan-500 to-blue-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Goal
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default GoalsSection;
